import { cloneElement, memo, useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';

import { CallMergeOutlined } from '@mui/icons-material';

import { ModalUI } from '../Modal/Modal';
import { ButtonUi } from '../Button/Button';
import SimpleModalContent from '../SimpleModalContent/SimpleModalContent';

import { mergeDialogStyles } from './style';
import { deleteConfirmationStyle } from '../SimpleModalContent/style';

export const MergeDialog = memo((props) => {
  const {
    fromMergeId,
    fromMergeName,
    closeModal,
    mergeFromTitle,
    AutocompleteComponent,
    onMerge,
  } = props;
  const { t } = useTranslation();

  const { isLoading } = useSelector((state) => state.organizations);

  const [mergeItem, setMergeItem] = useState(null);
  const [mergeConfirmationOpen, setMergeConfirmationOpen] = useState(false);

  const { mergeDialog, select } = mergeDialogStyles;
  const { deleteConfirmation } = deleteConfirmationStyle;

  /**
   * Sets merge item on select
   */
  const selectMergeItem = useCallback((item) => {
    setMergeItem(item);
  }, []);

  /**
   * Open merge confirmation dialog
   */
  const openMergeConfirmation = useCallback(
    () => setMergeConfirmationOpen(true),
    [],
  );

  /**
   * Close merge confirmation dialog
   */
  const closeMergeConfirmation = useCallback(
    () => setMergeConfirmationOpen(false),
    [],
  );

  /**
   * Calls merge callback and closes merge confirmation dialog
   */
  const organizationsMerge = useCallback(() => {
    onMerge(fromMergeId, mergeItem?.value);
    setMergeConfirmationOpen(false);
    setTimeout(() => closeModal(), 500);
  }, [closeModal, fromMergeId, mergeItem, onMerge]);

  return (
    <>
      <Box sx={mergeDialog}>
        <Box className='merge-visual'>
          <CallMergeOutlined className='merge-icon' />
        </Box>
        <Box className='content'>
          <Box className='label-value-row'>
            <Typography variant='h5' className='data-label'>
              {mergeFromTitle}:
            </Typography>
            <Typography variant='h5' className='data-value'>
              {fromMergeName}
            </Typography>
          </Box>
          <Box className='label-value-row'>
            <Typography variant='h5' className='data-label'>
              {t('organizations.merge-to')}:
            </Typography>
            {cloneElement(AutocompleteComponent, {
              selfSx: select,
              handleChange: selectMergeItem,
            })}
          </Box>
        </Box>
        <Box className='content-footer'>
          <ButtonUi
            className='main-btn'
            variant='outlined'
            type='input'
            size='small'
            disabled={!mergeItem}
            onClickHandler={openMergeConfirmation}
          >
            {t('buttons.merge')}
          </ButtonUi>
          <ButtonUi
            className='secondary-btn'
            variant='outlined'
            type='secondary'
            size='small'
            onClickHandler={closeModal}
          >
            {t('buttons.cancel')}
          </ButtonUi>
        </Box>
      </Box>
      {/* Delete confirmation modal */}
      <ModalUI
        modalSx={deleteConfirmation}
        open={mergeConfirmationOpen}
        isCloseBtn={false}
        onCloseHandler={closeMergeConfirmation}
        className='confirmation-modal'
        modalTitle={t('alerts.titles.organization-merge-confirmation')}
      >
        <SimpleModalContent
          content={[
            {
              translation: t('alerts.confirm.merge-organization', {
                interpolation: { escapeValue: false },
                mergeOrganization: fromMergeName,
                destinationOrg: mergeItem?.text,
              }),
              isHtml: true,
            },
          ]}
          isLoading={isLoading}
          mainBtn='buttons.confirm'
          onMainBtnClick={organizationsMerge}
          secondaryBtn='buttons.cancel'
          onSecondaryBtnClick={closeMergeConfirmation}
        />
      </ModalUI>
    </>
  );
});
